import { useTranslation } from "react-i18next";

import { CHECKLIST_TYPES, DESK_COMMUNICATION_EVENTS, KIOSK_COMMUNICATION_EVENT_TYPES } from "models";
import { useActivityLogs } from "modules/AppointmentDetails/components/ActivityLog/utils";

import type { Appointment } from "models";
import type { OrderedGroupedCheck } from "modules/AppointmentDetails/hooks";

export const LOG_OPTION_KEYS = {
  ActivityLog: "activity_log",
  CustomerSignLogs: "customer_sign_logs"
};

export enum APPOINTMENT_REPORT_SECTION_TYPE {
  Check = 1,
  Logs
}

export type CheckOption = {
  sectionType: APPOINTMENT_REPORT_SECTION_TYPE.Check;
  text: string;
  icon: string;
  key: number;
  value: number;
};

export type LogOption = {
  sectionType: APPOINTMENT_REPORT_SECTION_TYPE.Logs;
  text: string;
  key: string;
  isCompositeIcon: boolean;
};

const getCheckIcon = (isTyreCheck: boolean, isExtraPart: boolean) => {
  if (isTyreCheck) return "tire";
  if (isExtraPart) return "briefcase";

  return "list";
};

export const useDropdownOptions = (checks: OrderedGroupedCheck[] = [], appointment: Appointment) => {
  const { t } = useTranslation();

  const activityLogs = useActivityLogs(appointment);

  const checkOptions: CheckOption[] = checks.map(check => {
    const isExtraPart = check.checklist?.checklist_type === CHECKLIST_TYPES.ExtraParts;
    return {
      sectionType: APPOINTMENT_REPORT_SECTION_TYPE.Check,
      text: isExtraPart ? t("v8_extra_parts", "Extra parts") : check.checklist?.name || "",
      icon: getCheckIcon(check._isTyreCheck, isExtraPart),
      key: check.id,
      value: check.id
    };
  });

  const hasDeskSignature = !!appointment.desk_communications?.some(deskCommunication =>
    deskCommunication.events?.some(event => event.type === DESK_COMMUNICATION_EVENTS.DeskCheckInAnswered && !!event.customer_signature)
  );

  const hasKioskSignature = !!appointment.kiosk_communications?.some(kioskCommunication =>
    kioskCommunication.events?.some(event => event.type === KIOSK_COMMUNICATION_EVENT_TYPES.CheckInAnswered && !!event.customer_signature)
  );

  const hasSignatureEvent = hasDeskSignature || hasKioskSignature;

  const logOptions: LogOption[] = [];
  if (activityLogs.length) {
    logOptions.push({
      sectionType: APPOINTMENT_REPORT_SECTION_TYPE.Logs,
      text: t("v8_activity_log", "Activity Log"),
      key: LOG_OPTION_KEYS.ActivityLog,
      isCompositeIcon: false
    });
  }
  if (hasSignatureEvent) {
    logOptions.push({
      sectionType: APPOINTMENT_REPORT_SECTION_TYPE.Logs,
      text: t("v8_communication_sign_logs", "Communication Sign Logs"),
      key: LOG_OPTION_KEYS.CustomerSignLogs,
      isCompositeIcon: true
    });
  }

  return [...checkOptions, ...logOptions];
};
